import {hasActiveUpcomingCourses} from "./courseValidation";

/**
 * Utility functions for filtering, grouping, and searching published instructors.
 */

export const getSafeCounty = (instructor) => {
    if (!instructor || typeof instructor.county !== "string") {
        return "Øvrige";
    }
    const trimmed = instructor.county.trim();
    return trimmed.length > 0 ? trimmed : "Øvrige";
};

export const extractCountiesWithCount = (instructors) => {
    if (!Array.isArray(instructors) || instructors.length === 0) {
        return [{ county: "Alle", count: 0 }];
    }

    const counts = {};
    for (const instructor of instructors) {
        const county = getSafeCounty(instructor);
        counts[county] = (counts[county] || 0) + 1;
    }

    const sortedCounties = Object.keys(counts).sort((a, b) => a.localeCompare(b, "nb"));
    const countyList = sortedCounties.map(county => ({
        county,
        count: counts[county],
    }));

    return [{ county: "Alle", count: instructors.length }, ...countyList];
};

export const filterInstructors = (instructors, { searchTerm = "", selectedCounty = "Alle", onlyWithUpcomingCourses = false } = {}) => {
    if (!Array.isArray(instructors)) {
        return [];
    }

    const normalizedQuery = (searchTerm || "").toLowerCase().trim();
    const isAllCounty = !selectedCounty || selectedCounty === "Alle";

    return instructors
        .filter(instructor => {
            if (!instructor) return false;

            if (onlyWithUpcomingCourses && !hasActiveUpcomingCourses(instructor)) {
                return false;
            }

            const county = getSafeCounty(instructor);

            if (!isAllCounty && county !== selectedCounty) {
                return false;
            }

            if (!normalizedQuery) {
                return true;
            }

            const name = (instructor.name || "").toLowerCase();
            const place = (instructor.place || "").toLowerCase();
            const countyLower = county.toLowerCase();

            return (
                name.includes(normalizedQuery) ||
                place.includes(normalizedQuery) ||
                countyLower.includes(normalizedQuery)
            );
        })
        .sort((a, b) => (a.name || "").localeCompare(b.name || "", "nb"));
};

/**
 * Validates and sanitizes a phone number for use in a tel: URI.
 * Rejects javascript:, data:, and any non-standard phone characters.
 * Requires at least 3 digits.
 */
export const getSafeTelHref = (phone) => {
    if (!phone || typeof phone !== "string") return null;
    const trimmed = phone.trim();
    if (/^(javascript|data|vbscript):/i.test(trimmed)) return null;
    const cleaned = trimmed.replace(/^tel:/i, "").trim();
    if (!/^[+()0-9\s.-]+$/.test(cleaned)) return null;
    const digitsOnly = cleaned.replace(/\D/g, "");
    if (digitsOnly.length < 3) return null;
    return `tel:${cleaned}`;
};

/**
 * Validates and sanitizes an email address for use in a mailto: URI.
 * Rejects javascript:, data:, CRLF header injections, and non-email formats.
 */
export const getSafeMailtoHref = (email) => {
    if (!email || typeof email !== "string") return null;
    const trimmed = email.trim();
    if (/^(javascript|data|vbscript):/i.test(trimmed)) return null;
    const cleaned = trimmed.replace(/^mailto:/i, "").trim();
    if (/[\r\n]/.test(cleaned)) return null;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(cleaned)) return null;
    return `mailto:${cleaned}`;
};

